import '@/assets/css/main.css';

import { msalInstance } from './util/authConfig';
import { msalPlugin } from './util/msalPlugin';
import { EventType, type EventMessage } from '@azure/msal-browser';
import { logAuthFailure } from './util/logger';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import VueFeather from 'vue-feather';
import PageHeaderVue from './components/ui/PageHeader.vue';
import Icon from '@/components/ui/Icon.vue';

import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

import App from './App.vue';
import router from './router';
import { useUserStore } from './stores/user.store';
import { initSentry } from './util/sentry';

const isMsalSilentFrame = (): boolean => {
    if (window.self === window.top) {
        return false;
    }

    if (/[#&](code|error|id_token|access_token)=/.test(window.location.hash)) {
        return true;
    }

    try {
        // Throws only for a cross-origin embedder, which is by definition not one
        // of our own same-origin renew frames.
        return window.frameElement?.classList.contains('msalSilentIframe') ?? false;
    } catch {
        return false;
    }
};

(async () => {

    // MSAL's silent-renew iframe loads this SPA at the redirectUri ('/'). Booting
    // the whole app inside that hidden iframe is waste, and worse: MSAL polls the
    // frame's URL for the auth response, so our own router replacing it ('/' →
    // '/dashboard') loses the hash MSAL is waiting for, which is exactly how a
    // silent refresh ends up hanging and taking every link on the page with it.
    // MSAL needs no JS in that frame, so leave it alone and boot nothing.
    //
    // Scoped to MSAL's own frame rather than "any frame", so deliberately
    // embedding this app somewhere still renders. Two signals, because the class
    // name is MSAL's internal detail and could change on upgrade: a framed page
    // sitting on an auth response is a renew frame either way.
    if (isMsalSilentFrame()) {
        return;
    }

    const app = createApp(App);

    // Initialise Sentry as early as possible so bootstrap-time failures are
    // captured. No-op in dev or without a DSN. `router` is already imported, so
    // navigation-span instrumentation works regardless of when app.use(router) runs.
    initSentry(app, router);

    app.component(VueFeather.name ?? 'vue-feather', VueFeather);
    app.component('PageHeader', PageHeaderVue);
    app.component('Icon', Icon);

    // Create pinia store
    const pinia = createPinia();
    // Use pinia plugin for persisted state
    pinia.use(piniaPluginPersistedstate);
    // Register pinia
    app.use(pinia);

    const userStore = useUserStore();

    await msalInstance.initialize();

    try {
        const redirectResult = await msalInstance.handleRedirectPromise();

        if (redirectResult && redirectResult.account) {
            userStore.setAuthResult(redirectResult);
            msalInstance.setActiveAccount(redirectResult.account);
        }
    } catch (err) {
        logAuthFailure('msal_login_failure', { phase: 'handleRedirectPromise' }, err);
    }

    await userStore.bootstrapFromMsal();

    msalInstance.addEventCallback((event: EventMessage) => {
        // Make MSAL-level failures observable for diagnostics/telemetry.
        switch (event.eventType) {
            case EventType.ACQUIRE_TOKEN_FAILURE:
                logAuthFailure('msal_acquire_token_failure', {}, event.error);
                break;
            case EventType.LOGIN_FAILURE:
                logAuthFailure('msal_login_failure', {}, event.error);
                break;
            case EventType.SSO_SILENT_FAILURE:
                logAuthFailure('msal_sso_silent_failure', {}, event.error);
                break;
        }

        void userStore.ensureLoginState(event);
    });

    // Register MSAL plugin
    app.use(msalPlugin, msalInstance);

    // Register router
    app.use(router);

    app.mount('#app');
})();
